/**
 * A file to the Storage path an edge function reserved for it: Storage's resumable upload (TUS), in 6 MB pieces,
 * signed by the reservation's token alone. Shared by every reserved upload — a knowledge-base file, a campaign's
 * contacts. The server half is `supabase/functions/_shared/resumable-upload.ts`, which answers the endpoint.
 */

import { Upload } from "tus-js-client";

export const UPLOAD_FAILED = "The file could not be uploaded. Check your connection and try again.";

/** Storage's resumable upload takes a file in pieces of exactly this size. */
const PIECE_BYTES = 6 * 1024 * 1024;

/** Where a reservation says the file goes. */
export interface UploadTarget {
  resumable_url: string;
  /** The upload's only credential. */
  token: string;
  bucket: string;
  path: string;
  content_type: string;
}

/**
 * The file to its reserved object, in 6 MB pieces: a piece that fails is sent again after 0, 3, 5, 10 and 20 s,
 * from the last byte Storage holds. Reports the percent sent as it goes.
 */
export function sendFile(upload: UploadTarget, file: Blob, onProgress: (percent: number) => void) {
  return new Promise<void>((resolve, reject) => {
    new Upload(file, {
      endpoint: upload.resumable_url,
      headers: { "x-signature": upload.token },
      metadata: {
        bucketName: upload.bucket,
        objectName: upload.path,
        contentType: upload.content_type,
        cacheControl: "3600",
      },
      chunkSize: PIECE_BYTES,
      retryDelays: [0, 3000, 5000, 10000, 20000],
      uploadDataDuringCreation: true,
      // A reservation is one upload: there is nothing to find again after a reload.
      storeFingerprintForResuming: false,
      onProgress: (sent, total) => onProgress(Math.round((sent / total) * 100)),
      onSuccess: () => resolve(),
      onError: () => reject(new Error(UPLOAD_FAILED)),
    }).start();
  });
}
